/**
 * Déchiffrement de token hybride RSA-OAEP + AES-GCM
 * Inspiré de tes utilitaires existants.
 */

// --- Types ---
export interface EncryptedTokenPacket {
    key: string;   // base64: AES key chiffrée par RSA-OAEP
    nonce: string; // base64: IV (12 octets)
    tag: string;   // base64: tag GCM (16 octets)
    data: string;  // base64: ciphertext (sans le tag)
}

// Garde: vérifie vite que l’objet ressemble au paquet attendu
export function isEncryptedTokenPacket(x: any): x is EncryptedTokenPacket {
    return !!(
        x &&
        typeof x === 'object' &&
        typeof x.key === 'string' &&
        typeof x.nonce === 'string' &&
        typeof x.tag === 'string' &&
        typeof x.data === 'string'
    );
}

// --- Helpers ---
function base64ToArrayBuffer(b64: string): ArrayBuffer {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
}

function concatArrayBuffer(a: ArrayBuffer, b: ArrayBuffer): ArrayBuffer {
    const ua = new Uint8Array(a), ub = new Uint8Array(b);
    const out = new Uint8Array(ua.length + ub.length);
    out.set(ua, 0);
    out.set(ub, ua.length);
    return out.buffer;
}

/**
 * Importe une clé privée RSA PKCS#8 PEM pour RSA-OAEP
 * hashAlgo: 'SHA-256' (par défaut) ou 'SHA-1'
 */
async function importRSAPrivateKeyPKCS8(
    pemKey: string,
    hashAlgo: 'SHA-256' | 'SHA-1' = 'SHA-256'
): Promise<CryptoKey> {
    const header = '-----BEGIN PRIVATE KEY-----';
    const footer = '-----END PRIVATE KEY-----';
    const raw = pemKey.replace(header, '').replace(footer, '').replace(/\s+/g, '');
    const der = base64ToArrayBuffer(raw);
    return crypto.subtle.importKey(
        'pkcs8',
        der,
        { name: 'RSA-OAEP', hash: hashAlgo },
        false,
        ['decrypt']
    );
}

/**
 * Déchiffre le paquet de token et renvoie le "plaintext" sous forme de string.
 * - Si ton backend renvoie un JWT -> tu obtiens la chaîne JWT.
 * - S’il renvoie un JSON stringifié -> tu récupères la chaîne JSON.
 *
 * @param packet       Paquet {key, nonce, tag, data} en base64
 * @param privatePem   Clé privée RSA PKCS#8 (PEM)
 * @param trySha1      Essaie RSA-OAEP/SHA-1 en fallback si SHA-256 échoue (par défaut: true)
 */
export async function decryptTokenPacketToString(
    packet: EncryptedTokenPacket,
    privatePem: string,
    trySha1: boolean = true
): Promise<string> {
    if (!isEncryptedTokenPacket(packet)) {
        throw new Error('Paquet token invalide: {key, nonce, tag, data} requis.');
    }

    // 1) Déchiffrer la clé AES avec RSA-OAEP
    const encAesKey = base64ToArrayBuffer(packet.key);

    let rawAesKey: ArrayBuffer | null = null;
    // Essai SHA-256 d’abord
    try {
        const rsaPriv256 = await importRSAPrivateKeyPKCS8(privatePem, 'SHA-256');
        rawAesKey = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, rsaPriv256, encAesKey);
    } catch (e256) {
        if (!trySha1) throw new Error(`RSA-OAEP SHA-256 a échoué: ${(e256 as Error)?.message || e256}`);
        // Fallback SHA-1
        try {
            const rsaPriv1 = await importRSAPrivateKeyPKCS8(privatePem, 'SHA-1');
            rawAesKey = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, rsaPriv1, encAesKey);
        } catch (e1) {
            throw new Error(
                `Échec du déchiffrement RSA-OAEP (SHA-256 & SHA-1): ${(e1 as Error)?.message || e1}`
            );
        }
    }
    if (!rawAesKey) throw new Error('Clé AES non récupérée.');

    // 2) Importer la clé AES-GCM
    const aesKey = await crypto.subtle.importKey(
        'raw',
        rawAesKey,
        { name: 'AES-GCM' },
        false,
        ['decrypt']
    );

    // 3) AES-GCM : concat(ciphertext, tag) pour WebCrypto
    const iv   = base64ToArrayBuffer(packet.nonce);
    const data = base64ToArrayBuffer(packet.data);
    const tag  = base64ToArrayBuffer(packet.tag);
    const ctWithTag = concatArrayBuffer(data, tag);

    const plainBuf = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, tagLength: 128 },
        aesKey,
        ctWithTag
    );

    return new TextDecoder().decode(new Uint8Array(plainBuf));
}

/**
 * Confort: Déchiffre et retourne soit le JWT string, soit l’attribut
 * {access_token|token} si le plaintext est un JSON.
 */
export async function decryptTokenPacketToBearer(
    packet: EncryptedTokenPacket,
    privatePem: string
): Promise<string> {
    const plaintext = await decryptTokenPacketToString(packet, privatePem);
    // Si c’est du JSON, extraire access_token|token
    try {
        const maybe = JSON.parse(plaintext);
        return (maybe?.access_token ?? maybe?.token ?? plaintext) as string;
    } catch {
        return plaintext; // déjà un JWT string
    }
}
